import { FC, useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import isEmpty from "lodash/isEmpty";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";

import { useBooking } from "@hooks/useBooking/useBooking";
import { useBookingSubmit } from "@hooks/useBookingSubmit/useBookingSubmit";
import { usePricing } from "@hooks/usePricing/usePricing";
import { BookingFormType } from "@models/components/booking.model";
import {
  getLocationType,
  locationType,
  paymentRef,
} from "@models/services/organisation";
import { userProfile } from "@models/services/user";
import { ContactDetailsType } from "@models/shared/shared.model";
import { RootState } from "@store/index";

import Pricing from "./Pricing/Pricing";
import Terms from "./Terms/Terms";

const BookingForm: FC<BookingFormType> = ({ setGuestError, urls }) => {
  const { scrollFocusToPayment } = useBooking();
  const { getPriceByResourceSlot, priceLoader } = usePricing();
  const {
    gdprAccepted,
    legalTermsAccepted,
    paymentLoader,
    resourcePrices,
    selectedSlot,
  } = useSelector((State: RootState) => State.booking);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const context: [
    getLocationType,
    locationType,
    userProfile,
    ContactDetailsType,
    paymentRef,
  ] = useOutletContext();

  const { handleSubmit } = useForm({
    defaultValues: {},
    mode: "onTouched",
  });

  const { resourceId, slot } = selectedSlot;

  const { guestPriceAmount, priceForResource, totalPriceWithTax } =
    useMemo(() => {
      const {
        guestPrice = 0,
        price = 0,
        totalPriceWithTax = 0,
      } = getPriceByResourceSlot(resourcePrices, resourceId, slot?.slot) || {};

      const numPrice = Number(price);
      const numGuestPrice = Number(guestPrice);

      return {
        guestPriceAmount: Number.isNaN(numGuestPrice) ? 0 : numGuestPrice,
        priceForResource: Number.isNaN(numPrice) ? 0 : numPrice,
        totalPriceWithTax,
      };
    }, [resourcePrices, resourceId, slot?.slot, getPriceByResourceSlot]);

  const { handleBookingSubmit } = useBookingSubmit({
    selectedPaymentMethod,
    setGuestError,
    totalPriceWithTax,
  });

  useEffect(() => {
    scrollFocusToPayment();
  }, []);

  useEffect(() => {
    if (guestPriceAmount + priceForResource <= 0) setSelectedPaymentMethod("");
  }, [guestPriceAmount, priceForResource]);

  const onSubmit = () => {
    handleBookingSubmit();
  };

  const isPaymentRequired = priceForResource + guestPriceAmount > 0;

  const isSlotInvalid =
    isEmpty(selectedSlot) || selectedSlot?.isBookable === false;

  const isBookingDisabled =
    !gdprAccepted ||
    !legalTermsAccepted ||
    (isPaymentRequired && !selectedPaymentMethod) ||
    paymentLoader ||
    priceLoader ||
    isSlotInvalid;

  return (
    <>
      <h4 className="mt-2 fg-booking-subheading">{t("confirmBooking")}</h4>

      <Pricing
        payment={{
          selectedPaymentMethod: selectedPaymentMethod,
          setSelectedPaymentMethod: setSelectedPaymentMethod,
        }}
      />

      {/* TODO This block should be moved to a separate component */}
      <div
        className="fg-tab-unit-list fg-border-none mb-2"
        data-testid="booking-form-terms"
      >
        <Terms
          gdpr={urls?.gdpr}
          legal_terms={urls?.legal_terms}
          gdprTerms={urls?.gdprTerms}
          termsService={urls?.termsService}
        />
        <div className="row" ref={context?.[4] ?? null}>
          <div className="col-12 col-xl-7">
            <div className="row fg-modify-btn-wrap">
              <div className="col-sm-12 col-md-6">
                <button
                  className="btn fg-btn fg-btn-filled-darkblue w-100 "
                  data-testid="booking-button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isBookingDisabled}
                >
                  {t("bookOrModify")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingForm;
